// Avoid re-exports in favor of direct imports or entry-point files
/* eslint-disable no-barrel-files/no-barrel-files */

import type { ExperienceName } from '@confluence/experience-tracker/entry-points/ExperienceNameType.ts';
import {
	EDIT_PAGE_EXPERIENCE,
	HOME_EXPERIENCE,
	SPACE_OVERVIEW_EXPERIENCE,
	VIEW_PAGE_EXPERIENCE,
	WHITEBOARD_EXPERIENCE,
	DATABASE_EXPERIENCE,
	EXTERNAL_SHARE_EXPERIENCE,
	CONTENT_HISTORY_EXPERIENCE,
	SPACE_PAGES_EXPERIENCE,
	EDIT_PAGE_EMBED_EXPERIENCE,
	EMBED_EXPERIENCE,
	EDIT_PAGE_SMART_CREATE_LOAD_EXPERIENCE,
	FRONT_COVER_LOAD_EXPERIENCE,
	PDF_EXPORT_PAGE_EXPERIENCE,
	ADVANCED_SEARCH_EXPERIENCE,
	ATTACHMENTS_PAGE_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { preloadPageLayout } from '@confluence/page-layout/entry-points/preloadPageLayout.ts';
import { preloadAppNavigation } from '@confluence/app-navigation/entry-points/preloadAppNavigation.ts';
import { preloadUpgradeFreePoolTargetExperimentPreUpgradeModalQuery } from '@confluence/experiment-upgrade-free-pool-target/entry-points/pre-upgrade-modal-graphql-query.ts';
import { preloadPremiumEndOfTrialModalEligibilityQuery } from '@confluence/experiment-ai-in-editor-eot/entry-points/premium-end-of-trial-modal-eligibility-query.ts';
import { preloadTopNavigation } from '@confluence/app-navigation/entry-points/preloadTopNavigation.ts';
import { preloadOperations } from '@confluence/site-operations/entry-points/preloadOperations.ts';
import { preloadCookieCategories } from '@confluence/storage-manager/entry-points/preloadCookieCategories.ts';
import { preloadPreferences } from '@confluence/storage-manager/entry-points/preloadPreferences.ts';
import { preloadLocalStorage } from '@confluence/preload-storage-queries/entry-points/preloadLocalStorage.ts';
import { getExtensionManifest } from '@confluence/fabric-extension-lib/entry-points/getExtensionManifest.ts';
import { preloadMacrosSPA } from '@confluence/fabric-extension-handlers/entry-points/preloadMacrosSPA.ts';
import { preloadSuperBatchData } from '@confluence/wrm/entry-points/preloadSuperBatchData.ts';
import { preloadCookiePreferences } from '@confluence/cookies-consent-banner/entry-points/preloadCookiePreferences.ts';
import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities/entry-points/SSRPrepareErrorsClient.ts';
import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs.ts';
import { preloadSpaceDetail } from '@confluence/space-utils/entry-points/index.ts';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { createDataFetcherForSuspense } from '@confluence/query-preloader-tools/entry-points/dataFetcherForSuspense.ts';
import { setFallbackAtlExperience } from '@confluence/graphql/entry-points/setFallbackAtlExperience.ts';
import { preloadUserSettingsForSSR } from '@confluence/preload-storage-queries/entry-points/preloadUserSettingsForSSR.ts';
import { preloadGlobalNavigationComponent } from '@confluence/side-navigation/entry-points/preloadGlobalNavigationComponent.ts';
import { preloadGlobalAppShortcutsLicensedProducts } from '@confluence/side-navigation/entry-points/preloadGlobalAppShortcutsLicensedProducts.ts';
import { fg } from '@confluence/feature-gating';
import { expVal, UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { getCachedPageData } from '@confluence/live-pages-utils/entry-points/getCachedPageData.ts';
import { preloadUseConfluenceStandardToPremiumExperimentQuery } from '@confluence/experiment-edition-awareness-for-standard-to-premium/entry-points/preloadUseConfluenceStandardToPremiumExperimentQuery.ts';
import { preloadEditorPreferenceQuery } from '@confluence/editor-presets/entry-points/EditorPreferencesQuery.ts';
import { preloadLivePageRoute } from '@confluence/live-page-route-preloader/entry-points/preloadLivePageRoute.ts';
import { preloadShortcutEnabledState } from '@confluence/shortcuts/entry-points/preloadShortcutEnabledState.ts';
import { preloadRovoAnnouncementOnboardingState } from '@confluence/onboarding-helpers/entry-points/preloadRovoAnnouncementOnboardingState.ts';
import { PreloadingMode } from '@confluence/hover-preloader';
import {
	matchHome,
	matchViewPage,
	matchSpaceOverview,
	matchCompanyHub,
	matchFabricEditor,
	matchExternalShare,
	matchSpacePages,
	matchWhiteboardPage,
	matchDatabasePage,
	matchEmbedPage,
	matchContentHistory,
	matchAttachmentsPage,
	matchEmbeddedEditor,
	matchSmartCreateEditor,
	matchPdfExport,
	matchSearch,
} from '@confluence/query-preloaders-common/entry-points/matchRoutes.ts';

import { preloadViewPageRoute } from './preloadViewPageRoute.ts';
import {
	preloadSpaceOverviewRoute,
	preloadSpaceOverviewRouteCritical,
} from './preloadSpaceOverviewRoute.ts';
import { preloadWhiteboardRoute } from './preloadWhiteboardRoute.ts';
import { preloadDatabaseRoute } from './preloadDatabaseRoute.ts';
import { preloadSmartCreateEditorRoute } from './preloadSmartCreateEditorRoute.ts';
import { preloadEmbedRoute } from './preloadEmbedRoute.ts';
import { preloadEditPageRoute } from './preloadEditPageRoute.ts';
import { preloadEditPageRoutePostSSR } from './preloadEditPageRoutePostSSR.ts';
import { preloadSpacePagesRoute } from './preloadSpacePagesRoute.ts';
import { preloadContentHistoryRoute } from './preloadContentHistoryRoute.ts';
import { preloadAttachmentsPageRoute } from './preloadAttachmentsPageRoute.ts';
import { preloadHomeRoute } from './preloadHomeRoute.ts';
import { preloadBanners } from './preloadBanners.tsx';
import { preloadScreenSpaceFlags } from './preloadScreenSpaceFlags.ts';
import type { PreloadOptions } from './preloadTypes.ts';
import { preloadCommonDataForRouteWithSpaceKey } from './preloadCommonDataForRouteWithSpaceKey.ts';
import { preloadAdvancedSearchRoute } from './preloadAdvancedSearchRoute.ts';
import { getEmbeddedWhiteboardContext } from './getEmbeddedWhiteboardContext.ts';
/**
 * Preload the extension manifest and legacy macros whenever these declared to be preloaded
 * by the server-side code. Since View Page, Space Overview and Editor are the only routes
 * using extensions we can preload it only for those.
 *
 * WARN: this function is called always, no matter whether server-hydrated Apollo cache exists
 * or not, as data preloaded here are not expected to be available from server even after SSR.
 */
export async function preloadExtensionsData(url: string) {
	const match = matchRoute(url);
	if (
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		!process.env.REACT_SSR &&
		(match?.experience === VIEW_PAGE_EXPERIENCE ||
			match?.experience === SPACE_OVERVIEW_EXPERIENCE ||
			match?.experience === EDIT_PAGE_EXPERIENCE)
	) {
		return waitForAllReturnWhenError([
			getExtensionManifest({ spaceKey: match.params.spaceKey }),
			preloadSpaceDetail(match.params.spaceKey),
			preloadSuperBatchData(),
			preloadMacrosSPA(),
		]);
	}
}

/**
 *
 *     _    _  ___ ______ _   _ _____ _   _ _____
 *    | |  | |/ _ \| ___ | \ | |_   _| \ | |  __ \
 *    | |  | / /_\ | |_/ |  \| | | | |  \| | |  \/
 *    | |/\| |  _  |    /| . ` | | | | . ` | | __
 *    \  /\  | | | | |\ \| |\  |_| |_| |\  | |_\ \
 *     \/  \/\_| |_\_| \_\_| \_/\___/\_| \_/\____/
 *
 *    All the preloader function calls should be chained to the returning promise.
 *    preloadQueryBasedOnRoute should ONLY be resolved when ALL the queries have been executed.
 *
 */
export async function preloadQueryBasedOnRoute(
	url,
	isTransition = false,
	previousMatch = null,
	preloadOptions: PreloadOptions | undefined = undefined,
): Promise<any> {
	const commonTasks: Promise<any>[] = [];

	const match = matchRoute(url);
	if (match?.experience) {
		setFallbackAtlExperience(match.experience);
	}

	const isEmbeddedWhiteboard =
		match?.experience === WHITEBOARD_EXPERIENCE &&
		getEmbeddedWhiteboardContext(match).bypassesMainLayout;

	/**
	 * There are synchronous operations in preparing session data.
	 * All queries that independent to session data should be fired first.
	 */
	if (!isTransition) {
		commonTasks.push(preloadCookiePreferences());
		if (fg('confluence_browser_storage_controls')) {
			commonTasks.push(preloadCookieCategories(), preloadPreferences());
		}

		if (match?.experience === PDF_EXPORT_PAGE_EXPERIENCE) {
			// The PDF export route only renders a specific Confluence page's title and content.
			// So, we don't need to fetch data for certain common Confluence features and preferences such as navigation and user settings.
			// Note 1: PDF export is not a SSR capability and also need not support SPA transitions, as it is only accessible via direct URL.
			commonTasks.push(
				// Remote local storage
				preloadLocalStorage(),
				// Forge UI user profile
				preloadOperations(),
			);
		} else if (isEmbeddedWhiteboard) {
			commonTasks.push(preloadLocalStorage());

			// https://switcheroo.atlassian.com/ui/gates/0d974cd3-c266-43ed-9312-0980df443012/key/post-office_screen-space-flags-confluence-ssr
			if (fg('post-office_screen-space-flags-confluence-ssr')) {
				commonTasks.push(preloadScreenSpaceFlags(match));
			}

			commonTasks.push(
				preloadUserSettingsForSSR({
					themeState: undefined,
				}),
			);

			commonTasks.push(preloadShortcutEnabledState());
		} else if (match?.experience !== EXTERNAL_SHARE_EXPERIENCE) {
			// external share doesn't have navigation, returns only errors
			commonTasks.push(preloadLocalStorage(), preloadBanners());
			commonTasks.push(preloadPageLayout());
			commonTasks.push(preloadGlobalAppShortcutsLicensedProducts());
			if (expVal('confluence_app_navigation_preloading_in_suspense', 'isEnabled', false)) {
				commonTasks.push(
					createDataFetcherForSuspense('top-nav', () => [
						preloadTopNavigation(),
						preloadAppNavigation(),
						preloadShortcutEnabledState(),
					]),
				);
			} else {
				commonTasks.push(preloadAppNavigation());
				commonTasks.push(
					createDataFetcherForSuspense('top-nav', () => [
						preloadTopNavigation(),
						preloadShortcutEnabledState(),
					]),
				);
			}
			commonTasks.push(preloadGlobalNavigationComponent());

			if (UNSAFE_expValEqualsNoExposure('platform_editor_controls', 'cohort', 'variant1')) {
				commonTasks.push(preloadEditorPreferenceQuery());
			}

			if (fg('twcg-206_confluence_upgrade_permanent_free_fg')) {
				commonTasks.push(preloadUpgradeFreePoolTargetExperimentPreUpgradeModalQuery());
			}

			if (fg('twcg-530_confluence_ai_in_editor_eot_gate')) {
				commonTasks.push(preloadPremiumEndOfTrialModalEligibilityQuery());
			}

			// https://switcheroo.atlassian.com/ui/gates/0d974cd3-c266-43ed-9312-0980df443012/key/post-office_screen-space-flags-confluence-ssr
			if (fg('post-office_screen-space-flags-confluence-ssr')) {
				commonTasks.push(preloadScreenSpaceFlags(match));
			}

			commonTasks.push(preloadOperations());

			const experience = match?.experience;
			commonTasks.push(
				preloadUserSettingsForSSR({
					themeState: [EDIT_PAGE_EMBED_EXPERIENCE, VIEW_PAGE_EXPERIENCE].includes(
						experience as string,
					)
						? (match?.query?.themeState as string)
						: undefined,
				}),
			);

			if (fg('edition_awareness_standard_upsell_confluence_ab_fg')) {
				commonTasks.push(preloadUseConfluenceStandardToPremiumExperimentQuery());
			}
		}
	}

	if (!match) {
		// Following code are specific to each route.
		// If we don't get a match here then there is nothing interesting
		return Promise.resolve();
	}

	let routeTasks: Promise<any> = Promise.resolve();
	const commonRouteTasks: Promise<any>[] = [];

	if (expVal('confluence_restrictions_fix', 'isEnabled', false)) {
		// Routes can have common data if the route has a space key
		const spaceKey = match?.params?.spaceKey;
		if (spaceKey) {
			commonRouteTasks.push(preloadCommonDataForRouteWithSpaceKey(spaceKey));
		}
		// TODO: We should do similar for content id here
		// but currently preloadPageRestrictionsQuery is spread out in each individual route.
	}

	if (!isEmbeddedWhiteboard && fg('aifc_onboarding_ga_enabled')) {
		commonRouteTasks.push(preloadRovoAnnouncementOnboardingState());
	}

	if (match.experience === VIEW_PAGE_EXPERIENCE) {
		const { isLivePage: isLivePageSSR } = getSSRRenderInputs();

		// On transition, validate whether we're a live doc. If so, only preload live doc routes, not all view page routes. Prevents unnecessary GQL calls, e.g. ContentUnifiedQuery.
		let isLivePageTransition = false;
		const contentId = match?.params?.contentId;
		if (contentId && !isLivePageSSR) {
			const { subType, isArchived } = getCachedPageData(contentId);
			isLivePageTransition = isTransition && subType === 'live' && isArchived === false;
		}

		routeTasks =
			isLivePageSSR || isLivePageTransition
				? preloadLivePageRoute(match, url)
				: preloadViewPageRoute(match, url, isTransition, previousMatch);
	} else if (
		match.experience === SPACE_OVERVIEW_EXPERIENCE ||
		match.experience === FRONT_COVER_LOAD_EXPERIENCE
	) {
		if (preloadOptions?.preloadingMode === PreloadingMode.onlyJSAndCriticalData) {
			routeTasks = preloadSpaceOverviewRouteCritical(match);
		} else {
			routeTasks = preloadSpaceOverviewRoute(match, previousMatch, url);
		}
	} else if (match.experience === WHITEBOARD_EXPERIENCE) {
		routeTasks = preloadWhiteboardRoute(match, url, isTransition);
	} else if (match.experience === DATABASE_EXPERIENCE) {
		routeTasks = preloadDatabaseRoute(match, url, isTransition);
	} else if (match.experience === EMBED_EXPERIENCE) {
		routeTasks = preloadEmbedRoute(match, url, isTransition);
	} else if (match.experience === EDIT_PAGE_SMART_CREATE_LOAD_EXPERIENCE) {
		routeTasks = preloadSmartCreateEditorRoute(match.params.contentId);
	} else if (match.experience === EDIT_PAGE_EXPERIENCE) {
		routeTasks = preloadEditPageRoute(match, url, isTransition);
	} else if (match.experience === HOME_EXPERIENCE) {
		routeTasks = preloadHomeRoute(match);
	} else if (match.experience === SPACE_PAGES_EXPERIENCE) {
		routeTasks = preloadSpacePagesRoute(match);
	} else if (match.experience === CONTENT_HISTORY_EXPERIENCE) {
		routeTasks = preloadContentHistoryRoute(match);
	} else if (match.experience === ATTACHMENTS_PAGE_EXPERIENCE) {
		routeTasks = preloadAttachmentsPageRoute(match);
	} else if (
		match.experience === ADVANCED_SEARCH_EXPERIENCE &&
		expVal('cc_search_route_preload', 'isEnabled', false)
	) {
		routeTasks = preloadAdvancedSearchRoute(match);
	}

	return waitForAllReturnWhenError([
		createDataFetcherForSuspense('default', () => [routeTasks, ...commonRouteTasks]),
		...commonTasks,
	]).then((errors) => {
		if (errors) {
			for (const e of errors) {
				if (process.env.NODE_ENV !== 'production') {
					// eslint-disable-next-line no-console
					console.log(`Error when preloading query:`, e.stack);
				}
				ssrPrepareErrorsClient.submitError(e);
			}
		}
	});
}

export async function preloadQueryBasedOnRoutePostSSR(pageUrl: string) {
	const match = matchRoute(pageUrl);
	if (!match) {
		return;
	}

	if (match.experience === EDIT_PAGE_EXPERIENCE) {
		return preloadEditPageRoutePostSSR(match, pageUrl);
	}
}
function matchRoute(url: string) {
	let match: ReturnType<typeof matchViewPage>;
	let experience: ExperienceName | undefined;

	if ((match = matchViewPage(url))) {
		experience = VIEW_PAGE_EXPERIENCE;
	} else if ((match = matchWhiteboardPage(url))) {
		experience = WHITEBOARD_EXPERIENCE;
	} else if ((match = matchSpaceOverview(url))) {
		experience = SPACE_OVERVIEW_EXPERIENCE;
	} else if ((match = matchCompanyHub(url))) {
		experience = FRONT_COVER_LOAD_EXPERIENCE;
	} else if ((match = matchHome(url))) {
		experience = HOME_EXPERIENCE;
	} else if ((match = matchFabricEditor(url))) {
		experience = EDIT_PAGE_EXPERIENCE;
	} else if ((match = matchExternalShare(url))) {
		experience = EXTERNAL_SHARE_EXPERIENCE;
	} else if ((match = matchSpacePages(url))) {
		experience = SPACE_PAGES_EXPERIENCE;
	} else if ((match = matchContentHistory(url))) {
		experience = CONTENT_HISTORY_EXPERIENCE;
	} else if ((match = matchAttachmentsPage(url))) {
		experience = ATTACHMENTS_PAGE_EXPERIENCE;
	} else if ((match = matchEmbeddedEditor(url))) {
		experience = EDIT_PAGE_EMBED_EXPERIENCE;
	} else if ((match = matchDatabasePage(url))) {
		experience = DATABASE_EXPERIENCE;
	} else if ((match = matchEmbedPage(url))) {
		experience = EMBED_EXPERIENCE;
	} else if ((match = matchSmartCreateEditor(url))) {
		experience = EDIT_PAGE_SMART_CREATE_LOAD_EXPERIENCE;
	} else if ((match = matchSearch(url))) {
		experience = ADVANCED_SEARCH_EXPERIENCE;
	} else if (matchPdfExport(url)) {
		match = matchPdfExport(url);
		experience = PDF_EXPORT_PAGE_EXPERIENCE;
	}

	return (
		match && {
			...match,
			experience,
		}
	);
}

export async function preloadQueriesForSPAHydration() {
	const pageUrl = window.location.toString();

	// Extensions Manifest and extensions data (when preloading is requested for them)
	// are heavy and should not be loaded on server side and embedded into HTML.
	void preloadExtensionsData(pageUrl);

	if (window.__SSR_RENDERED__) {
		void preloadQueryBasedOnRoutePostSSR(pageUrl);
	} else {
		void preloadQueryBasedOnRoute(pageUrl);
	}
}
